import { useState, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Bot, BookMarked, ShieldCheck, Sparkles, FileSearch, Search, Loader2, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import AICopilotPanel from '@/components/AICopilotPanel';
import { thesisApi } from '@/api/thesisApi';

/**
 * IntelligencePanel — Sprint 2, document-centered 3-pane layout.
 *
 * Right-hand sticky panel hosting tabs that follow the user across the writing
 * workspace. The Copilot tab is the default and is multi-turn / project-scoped.
 *
 * Other tabs surface focused views the user used to access via floating
 * dialogs. For now they remain entry points that open the existing dialogs —
 * over time the dialogs can be inlined into the panel.
 */
export default function IntelligencePanel({
  userId, projectId, chapterId, openChapter, language = 'en',
  onOpenEvidence, onOpenCompliance, onOpenReview, onOpenOriginality,
  onOpenDefense, onOpenGrounding,
  // Sprint 5 — inline AI Review state from parent
  reviewData, reviewLoading, applyingIssue, onApplyReviewIssue, onResetReview,
}) {
  const [tab, setTab] = useState('copilot');
  // Sprint 5 — inline Evidence Inspector state
  const [evQuery, setEvQuery] = useState('');
  const [evMode, setEvMode] = useState('hybrid');
  const [evResults, setEvResults] = useState([]);
  const [evLoading, setEvLoading] = useState(false);

  const runEvidenceSearch = useCallback(async () => {
    const q = evQuery.trim();
    if (!q || !projectId) return;
    setEvLoading(true);
    try {
      const res = await thesisApi.evidenceSearch(userId, projectId, q, { mode: evMode, n_results: 8 });
      setEvResults(res.results || []);
      if (!res.results?.length) toast.info('No matching chunks. Try a broader query or upload more documents.');
    } catch (e) {
      toast.error(`Evidence search failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setEvLoading(false);
    }
  }, [evQuery, evMode, projectId, userId]);

  // Auto-seed from active chapter title
  const seedFromChapter = () => {
    if (openChapter?.title) setEvQuery(openChapter.title);
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800" data-testid="intelligence-panel">
      <Tabs value={tab} onValueChange={setTab} className="flex flex-col h-full">
        <TabsList className="w-full grid grid-cols-5 h-auto p-1 bg-slate-50 dark:bg-slate-900/50 rounded-none border-b border-slate-200 dark:border-slate-800 gap-0.5">
          <TabsTrigger value="copilot" data-testid="ip-tab-copilot" className="flex flex-col gap-0.5 py-2 text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950">
            <Bot className="h-3.5 w-3.5" /> Copilot
          </TabsTrigger>
          <TabsTrigger value="evidence" data-testid="ip-tab-evidence" className="flex flex-col gap-0.5 py-2 text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950">
            <FileSearch className="h-3.5 w-3.5" /> Evidence
          </TabsTrigger>
          <TabsTrigger value="citations" data-testid="ip-tab-citations" className="flex flex-col gap-0.5 py-2 text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950">
            <BookMarked className="h-3.5 w-3.5" /> Citations
          </TabsTrigger>
          <TabsTrigger value="reviewer" data-testid="ip-tab-reviewer" className="flex flex-col gap-0.5 py-2 text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950">
            <Sparkles className="h-3.5 w-3.5" /> Reviewer
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="ip-tab-compliance" className="flex flex-col gap-0.5 py-2 text-[10px] data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950">
            <ShieldCheck className="h-3.5 w-3.5" /> Compliance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="copilot" className="flex-1 m-0 overflow-hidden">
          <AICopilotPanel userId={userId} projectId={projectId} chapterId={chapterId} language={language} />
        </TabsContent>

        <TabsContent value="evidence" className="flex-1 m-0 overflow-y-auto p-3 space-y-3" data-testid="ip-evidence-content">
          <div>
            <div className="flex gap-1.5">
              <Input
                data-testid="ip-evidence-query-input"
                placeholder="Search the project library…"
                value={evQuery}
                onChange={(e) => setEvQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runEvidenceSearch(); }}
                className="text-sm"
              />
              <Button size="icon" className="h-9 w-9" onClick={runEvidenceSearch} disabled={evLoading || !evQuery.trim()} data-testid="ip-evidence-search-btn">
                {evLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-1 text-[10px]">
                {['dense', 'hybrid', 'bm25'].map(m => (
                  <button key={m} type="button"
                    onClick={() => setEvMode(m)}
                    className={`px-2 py-0.5 rounded ${evMode === m ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}
                    data-testid={`ip-evidence-mode-${m}`}>
                    {m}
                  </button>
                ))}
              </div>
              {openChapter && (
                <button type="button" onClick={seedFromChapter}
                  className="text-[10px] text-blue-600 dark:text-blue-300 hover:underline"
                  data-testid="ip-evidence-seed-btn">
                  ↻ from chapter
                </button>
              )}
            </div>
          </div>

          {evResults.length > 0 ? (
            <div className="space-y-2" data-testid="ip-evidence-results">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                {evResults.length} chunks · {evMode}
              </p>
              {evResults.map((r, i) => (
                <div key={i} className="rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-2 bg-white dark:bg-slate-900 hover:border-blue-300 transition" data-testid={`ip-evidence-result-${i}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] font-mono text-slate-400">#{i + 1}</span>
                    {r.similarity !== undefined && (
                      <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">{(r.similarity * 100).toFixed(0)}%</span>
                    )}
                    <span className="text-[10px] text-slate-500 truncate flex-1">{r.source_title || '(untitled)'}</span>
                    {r.doi && (
                      <a href={`https://doi.org/${r.doi}`} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-blue-600 hover:underline truncate max-w-[100px]"
                        title={r.doi}>
                        DOI <ExternalLink className="h-2.5 w-2.5 inline" />
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-slate-700 dark:text-slate-300 line-clamp-3 leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          ) : (
            !evLoading && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-4 text-xs text-slate-500 dark:text-slate-400 text-center">
                <FileSearch className="h-5 w-5 mx-auto mb-1 opacity-50" />
                Search the project library to inspect retrieval ranking and chunk provenance.
              </div>
            )
          )}

          <button type="button" onClick={onOpenEvidence}
            className="w-full text-[10px] text-slate-500 hover:text-blue-600 underline pt-2"
            data-testid="ip-evidence-fullscreen">
            Open full inspector ↗
          </button>
        </TabsContent>

        <TabsContent value="citations" className="flex-1 m-0 overflow-y-auto p-4 space-y-3" data-testid="ip-citations-content">
          {openChapter ? (
            <>
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                <h3 className="text-sm font-semibold mb-2">Verification summary</h3>
                {openChapter.verification ? (
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Trust score</span>
                      <span className={`font-bold ${
                        openChapter.verification.summary?.trust_score >= 0.9 ? 'text-emerald-600 dark:text-emerald-400'
                        : openChapter.verification.summary?.trust_score >= 0.6 ? 'text-amber-600'
                        : 'text-red-600'
                      }`}>
                        {Math.round((openChapter.verification.summary?.trust_score || 0) * 100)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Verified</span>
                      <span className="font-mono">{openChapter.verification.summary?.verified || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Needs review</span>
                      <span className="font-mono text-amber-600">{openChapter.verification.summary?.suspect || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Unresolved</span>
                      <span className="font-mono text-red-600">{openChapter.verification.summary?.fabricated || 0}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic">Generate content first to see citation verification.</p>
                )}
              </div>
              {openChapter.citations?.length > 0 && (
                <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                  <h3 className="text-sm font-semibold mb-2">Sources ({openChapter.citations.length})</h3>
                  <ul className="space-y-1 text-xs">
                    {openChapter.citations.slice(0, 20).map((c, i) => (
                      <li key={i} className="truncate text-slate-600 dark:text-slate-400">
                        <span className="font-mono text-slate-400 mr-1">[{i + 1}]</span>
                        {c.source_title || c.doi || c.pmid || '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400 italic">Open a chapter to see its citations.</p>
          )}
        </TabsContent>

        <TabsContent value="reviewer" className="flex-1 m-0 overflow-y-auto p-4 space-y-3" data-testid="ip-reviewer-content">
          {openChapter ? (
            <>
              <div className="rounded-lg border border-rose-200 bg-rose-50/40 dark:bg-rose-950/20 dark:border-rose-900 p-3">
                <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-1">
                  <Sparkles className="h-3.5 w-3.5 text-rose-600" /> Defense Panel
                </h3>
                <p className="text-xs text-slate-700 dark:text-slate-300 mb-3">
                  Five AI reviewer perspectives critique your chapter as a doctoral committee would: <strong>Clinician · Statistician · Methodology · Journal · Committee</strong>.
                </p>
                <button type="button"
                  className="text-xs font-semibold px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={onOpenDefense}
                  data-testid="ip-open-defense-btn">
                  Simulate Defense
                </button>
              </div>

              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 dark:bg-indigo-950/20 dark:border-indigo-900 p-3">
                <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-1">
                  <Sparkles className="h-3.5 w-3.5 text-indigo-600" /> Claim Grounding
                </h3>
                <p className="text-xs text-slate-700 dark:text-slate-300 mb-3">
                  PaperQA2-style: extract every atomic claim, verify each against the project library, then optionally refine the draft with OpenScholar self-feedback.
                </p>
                <button type="button"
                  className="text-xs font-semibold px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={onOpenGrounding}
                  data-testid="ip-open-grounding-btn">
                  Ground claims
                </button>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50/40 dark:bg-amber-950/20 dark:border-amber-900 p-3">
                <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-1">
                  <Sparkles className="h-3.5 w-3.5 text-amber-600" /> Quick AI Review
                </h3>
                <p className="text-xs text-slate-700 dark:text-slate-300 mb-3">
                  Single-reviewer critique pass on the active chapter — flags methodology gaps, weak claims, missing citations.
                </p>
                <button type="button"
                  className="text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                  onClick={() => onOpenReview?.(openChapter.id)}
                  disabled={reviewLoading}
                  data-testid="ip-open-review-btn">
                  {reviewLoading ? <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Reviewing…</span> : 'Run AI Review'}
                </button>
                <button type="button"
                  className="ml-2 text-xs font-semibold px-3 py-1.5 rounded-md border border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  onClick={onOpenOriginality}
                  data-testid="ip-open-originality-btn">
                  Originality check
                </button>
                {reviewData && reviewData.chapter_id === openChapter.id && (
                  <button type="button"
                    className="ml-2 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 underline"
                    onClick={onResetReview}
                    data-testid="ip-review-reset-btn">
                    Reset
                  </button>
                )}
              </div>

              {/* Inline AI Review results (Sprint 5 — Step 12) */}
              {reviewData && reviewData.chapter_id === openChapter.id && (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-3" data-testid="ip-review-results-inline">
                  <div className="flex items-center gap-3 p-3 rounded-md bg-gradient-to-br from-amber-50 to-teal-50 dark:from-amber-950/30 dark:to-teal-950/30">
                    <div className={`text-3xl font-bold ${
                      reviewData.overall_score >= 8 ? 'text-emerald-600 dark:text-emerald-400' :
                      reviewData.overall_score >= 6 ? 'text-amber-600 dark:text-amber-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {reviewData.overall_score}
                      <span className="text-base text-slate-400">/10</span>
                    </div>
                    <p className="text-xs leading-relaxed flex-1 text-slate-700 dark:text-slate-300">{reviewData.summary}</p>
                  </div>

                  {reviewData.strengths?.length > 0 && (
                    <div data-testid="ip-review-strengths">
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-1 mb-1">
                        <CheckCircle2 className="h-3 w-3" /> Strengths
                      </p>
                      <ul className="space-y-0.5 text-xs text-slate-700 dark:text-slate-300">
                        {reviewData.strengths.map((s, i) => (
                          <li key={i} className="flex gap-1.5"><span className="text-emerald-500">•</span><span>{s}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {reviewData.issues?.length > 0 && (
                    <div data-testid="ip-review-issues">
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Issues ({reviewData.issues.length})
                      </p>
                      <div className="space-y-2">
                        {reviewData.issues.map((iss, i) => {
                          const sev = iss.severity || 'medium';
                          const sevClass = sev === 'high' ? 'border-l-red-500 bg-red-50/40 dark:bg-red-950/20'
                            : sev === 'medium' ? 'border-l-amber-500 bg-amber-50/40 dark:bg-amber-950/20'
                            : 'border-l-blue-400 bg-blue-50/40 dark:bg-blue-950/20';
                          const sevLabel = sev === 'high' ? 'Critical' : sev === 'medium' ? 'Medium' : 'Minor';
                          const isApplying = applyingIssue === i;
                          const isApplied = iss.applied;
                          return (
                            <div key={i} className={`border-l-4 px-2 py-2 rounded-r ${sevClass}`} data-testid={`ip-review-issue-${i}`}>
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[9px] uppercase tracking-widest font-bold opacity-70">{sevLabel}</span>
                                  {iss.where && <span className="text-[10px] font-mono text-slate-500 truncate max-w-[140px]">{iss.where}</span>}
                                </div>
                                {isApplied ? (
                                  <span className="text-[10px] font-semibold bg-emerald-600 text-white px-1.5 py-0.5 rounded">✓ Applied</span>
                                ) : (
                                  <button type="button"
                                    className="text-[10px] font-semibold px-2 py-0.5 rounded bg-teal-700 hover:bg-teal-800 text-white disabled:opacity-50"
                                    onClick={() => onApplyReviewIssue?.(i)}
                                    disabled={isApplying || applyingIssue !== null}
                                    data-testid={`ip-apply-suggestion-${i}`}>
                                    {isApplying ? <span className="flex items-center gap-0.5"><Loader2 className="h-2.5 w-2.5 animate-spin" />…</span> : 'Auto-fix'}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{iss.comment}</p>
                              {iss.suggestion && (
                                <p className="text-[10px] text-slate-600 dark:text-slate-400 italic mt-0.5">
                                  <strong>Fix:</strong> {iss.suggestion}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400 italic">Open a chapter to run AI review.</p>
          )}
        </TabsContent>

        <TabsContent value="compliance" className="flex-1 m-0 overflow-y-auto p-4 space-y-3" data-testid="ip-compliance-content">
          {openChapter ? (
            <div className="rounded-lg border border-teal-200 bg-teal-50/40 dark:bg-teal-950/20 dark:border-teal-900 p-3">
              <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-1">
                <ShieldCheck className="h-3.5 w-3.5 text-teal-600" /> EQUATOR compliance
              </h3>
              <p className="text-xs text-slate-700 dark:text-slate-300 mb-3">
                Check the active chapter against your reporting guideline (CONSORT, STROBE, PRISMA, TRIPOD, …). 426 checklist items across 11 guidelines.
              </p>
              <button type="button"
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-teal-700 hover:bg-teal-800 text-white"
                onClick={() => onOpenCompliance?.(openChapter)}
                data-testid="ip-open-compliance-btn">
                Run EQUATOR check
              </button>
            </div>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400 italic">Open a chapter to check compliance.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
